import React, { useRef, useState, useEffect } from 'react'
import {
  deleteImage,
  updateImage,
  uploadImages,
} from '../../../services/gallery'
import { useTranslation } from 'react-i18next'
import MyButton from '../buttons'
import { toast } from 'react-toastify'
import axios from 'axios'
import { Loader, RefreshCcw, Save, X } from 'lucide-react'
import MyModal from '../MyModal'
import { IoMdHelpCircleOutline } from 'react-icons/io'
import CookieManager from '../../lib/CookiesManager'
import { X_API_KEY } from '../../../services/api'

interface ImageToEdit {
  url: string
  fileName: string
}

interface ImageEditorProps {
  onClose: () => void
  onSave: () => void
  imageToEdit: ImageToEdit | null
}

const ImageEditor: React.FC<ImageEditorProps> = ({
  onClose,
  onSave,
  imageToEdit,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()
  const [drawing, setDrawing] = useState(false)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [isUploading, setIsUploading] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [brushSize, setBrushSize] = useState(30)
  const [previousDrawings, setPreviousDrawings] = useState<ImageData[]>([])
  const [originalDimensions, setOriginalDimensions] = useState({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    const loadImage = async () => {
      if (!imageToEdit || !imageToEdit.url) return

      try {
        setIsLoading(true)
        const token = CookieManager.getToken()

        const response = await axios.get(imageToEdit.url, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}`, 'X-API-KEY': X_API_KEY },
        })

        const imgUrl = URL.createObjectURL(response.data)
        const preloadImage = new Image()

        preloadImage.onload = () => {
          if (imageRef.current && containerRef.current) {
            const maxHeight =
              window.innerWidth <= 640 ? window.innerHeight * 0.6 : 400
            const maxWidth =
              window.innerWidth <= 640 ? window.innerWidth - 32 : 800

            setOriginalDimensions({
              width: preloadImage.naturalWidth,
              height: preloadImage.naturalHeight,
            })

            const aspectRatio =
              preloadImage.naturalWidth / preloadImage.naturalHeight

            let finalWidth = preloadImage.naturalWidth
            let finalHeight = preloadImage.naturalHeight

            if (finalHeight > maxHeight) {
              finalHeight = maxHeight
              finalWidth = maxHeight * aspectRatio
            }

            if (finalWidth > maxWidth) {
              finalWidth = maxWidth
              finalHeight = maxWidth / aspectRatio
            }

            const dimensions = {
              width: Math.floor(finalWidth),
              height: Math.floor(finalHeight),
            }

            setImageSize(dimensions)

            if (imageRef.current) {
              imageRef.current.src = imgUrl
            }

            setTimeout(() => initializeCanvas(preloadImage), 100)
          }
        }

        preloadImage.src = imgUrl
      } catch (error) {
        console.error('Error loading image:', error)
        toast.error(t('error-loading-image'))
      } finally {
        setIsLoading(false)
      }
    }

    loadImage()

    return () => {
      if (imageRef.current?.src) URL.revokeObjectURL(imageRef.current.src)
    }
  }, [imageToEdit?.url])

  const getScaledCoordinates = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }
  const initializeCanvas = (image: HTMLImageElement) => {
    const canvas = canvasRef.current
    if (!canvas) return

    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (ctx) {
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight)

      setPreviousDrawings([])
    }
  }

  const drawBlurCircle = (clientX: number, clientY: number, radius: number) => {
    const canvas = canvasRef.current
    if (!canvas || !imageRef.current) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const { x, y } = getScaledCoordinates(clientX, clientY)

    const displayRect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / displayRect.width
    const scaledRadius = radius * scaleX

    if (previousDrawings.length > 0) {
      ctx.putImageData(previousDrawings[previousDrawings.length - 1], 0, 0)
    } else {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height)
    }

    ctx.save()
    ctx.beginPath()
    ctx.arc(x, y, scaledRadius, 0, 2 * Math.PI)
    ctx.clip()
    ctx.filter = `blur(20px)`
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height)
    ctx.restore()

    setPreviousDrawings(prev => [
      ...prev,
      ctx.getImageData(0, 0, canvas.width, canvas.height),
    ])
  }

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setDrawing(true)
    drawBlurCircle(e.clientX, e.clientY, brushSize)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing) return
    drawBlurCircle(e.clientX, e.clientY, brushSize)
  }

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    setDrawing(true)
    const touch = e.touches[0]
    drawBlurCircle(touch.clientX, touch.clientY, brushSize)
  }

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    if (!drawing) return
    const touch = e.touches[0]
    drawBlurCircle(touch.clientX, touch.clientY, brushSize)
  }

  const handleMouseUp = () => setDrawing(false)

  const handleTouchEnd = () => setDrawing(false)

  const resetCanvas = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx && imageRef.current) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height)
      setPreviousDrawings([])
    }
  }

  const saveImage = async () => {
    if (!canvasRef.current || isUploading || !imageToEdit) return

    setIsUploading(true)

    try {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()

      if (userId && token) {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          toast.error(t('canvas-error'))
          setIsUploading(false)
          return
        }

        // ✅ Définir la taille max
        const MAX_WIDTH = 1200
        const MAX_HEIGHT = 1200

        let newWidth = canvas.width
        let newHeight = canvas.height

        if (canvas.width > MAX_WIDTH || canvas.height > MAX_HEIGHT) {
          const aspectRatio = canvas.width / canvas.height
          if (canvas.width > canvas.height) {
            newWidth = MAX_WIDTH
            newHeight = Math.round(MAX_WIDTH / aspectRatio)
          } else {
            newHeight = MAX_HEIGHT
            newWidth = Math.round(MAX_HEIGHT * aspectRatio)
          }
        }

        // ✅ Création d'un nouveau canvas pour redimensionner
        const resizedCanvas = document.createElement('canvas')
        resizedCanvas.width = newWidth
        resizedCanvas.height = newHeight

        const resizedCtx = resizedCanvas.getContext('2d')
        if (!resizedCtx) {
          toast.error(t('canvas-error'))
          setIsUploading(false)
          return
        }

        resizedCtx.drawImage(canvas, 0, 0, newWidth, newHeight)

        // ✅ Convertir en JPEG avec compression (qualité 80%)
        const dataURL = resizedCanvas.toDataURL('image/jpeg', 0.7)
        const response = await fetch(dataURL)
        const blob = await response.blob()

        // ✅ Vérifier si l'image dépasse 2MB
        if (blob.size > 2 * 1024 * 1024) {
          toast.error(t('image-too-large'))
          setIsUploading(false)
          return
        }

        const file = new File(
          [blob],
          imageToEdit.fileName.replace('.png', '.jpg'),
          {
            type: 'image/jpeg',
          },
        )

        // ✅ Envoyer l’image compressée
        const uploadedImage = await uploadImages(userId, token, [file])

        if (uploadedImage && uploadedImage.length > 0) {
          toast.success(t('blurred-image-saved-success'))
          await updateImage(
            userId,
            token,
            imageToEdit.fileName,
            uploadedImage[0].url,
          )
          await deleteImage(userId, token, imageToEdit.fileName)
          onSave()
          onClose()
        } else {
          toast.error(t('blurred-image-save-error'))
        }
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error(t('blurred-image-save-error'))
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <MyModal isOpen={true} onClose={onClose} size='large'>
      <div className='bg-white rounded-lg overflow-hidden'>
        <div className='text-lg text-center bg-anthracite gap-2 text-white px-4 py-2 rounded-t-lg flex items-center justify-start'>
          <span>{t('image-blurrer')}</span>
          <button
            type='button'
            onClick={() => setShowHelp(!showHelp)}
            className='text-white hover:text-gray-200 focus:outline-none'
          >
            <IoMdHelpCircleOutline className='h-6 w-6' />
          </button>
        </div>

        {showHelp && (
          <div
            className='mt-2 text-anthracite/70 text-sm mx-auto px-4 sm:px-0'
            style={{ width: '100%', maxWidth: imageSize.width }}
          >
            <p>{t('blur-instructions')}</p>
          </div>
        )}

        <div className='px-4 pb-4 pt-2 rounded-b-lg overflow-y-auto '>
          {isLoading ? (
            <div className='flex items-center justify-center '>
              <Loader className='h-8 w-8 text-anthracite animate-spin' />
            </div>
          ) : (
            <div
              ref={containerRef}
              className='relative border-4 cursor-crosshair border-dashed border-gray-400 rounded-lg mx-auto flex justify-center items-center w-full sm:w-auto'
              style={{ height: imageSize.height }}
            >
              <img
                ref={imageRef}
                alt={t('image-to-blur')}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                className='max-w-full max-h-full'
              />
              <canvas
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  touchAction: 'none',
                }}
              />
            </div>
          )}
          {!isLoading && (
            <div>
              <div className='mt-1'>
                <div className='flex justify-between items-center mb-2'>
                  <div className='relative w-full'>
                    <input
                      type='range'
                      min='5'
                      max='100'
                      value={brushSize}
                      onChange={e => setBrushSize(Number(e.target.value))}
                      className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer'
                    />
                  </div>
                </div>
              </div>

              <div className='mt-2 text-center flex justify-between'>
                <MyButton
                  type='button'
                  variant='redpink'
                  onClick={resetCanvas}
                  className='inline-flex items-center'
                >
                  <RefreshCcw className='h-4 w-4 mr-2' />
                  {t('reset')}
                </MyButton>
                <MyButton
                  type='button'
                  variant='whatsapp'
                  onClick={saveImage}
                  disabled={isUploading}
                  className='inline-flex items-center'
                >
                  {isUploading ? (
                    <Loader className='h-4 w-4 mr-2 animate-spin' />
                  ) : (
                    <Save className='h-4 w-4 mr-2' />
                  )}
                  {isUploading ? t('saving') : t('save')}
                </MyButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </MyModal>
  )
}

export default ImageEditor
